import { supabase } from '../clients/supabaseClient';

/**
 * Reads for the Arch sync pages.
 *
 * These go straight at the tables rather than through an Edge Function: the run
 * tables have RLS policies granting select to admin/super — the same predicate
 * the sync functions themselves check — so there is nothing an endpoint would add
 * beyond a hop.
 *
 * Both feeds keep a row per run rather than a single mutable row, so these are
 * histories. "How fresh is this store" is the newest row per store, not a column
 * somewhere.
 */

export type SyncStatus = 'PENDING' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
export type SyncTrigger = 'ADMIN' | 'CRON';

/**
 * One stock sync run, for one store.
 *
 * The counters matter as much as the status. A run can report SUCCEEDED having
 * received twenty thousand products and written none of them — that is what
 * `unmatched` exists to make visible, so the pages show the numbers rather than
 * just a green tick.
 */
export interface StockSyncRun {
    id: string;
    store_id: string;
    status: SyncStatus;
    trigger_type: SyncTrigger;
    /** The date sent to Arch, pinned for the life of the run. */
    last_updated_param: string;
    /** Products in the response Arch sent. Null if the fetch never succeeded. */
    products_received: number | null;
    /** Rows processed so far. products_received = stock_updated + rejected + unmatched. */
    fetched: number;
    stock_updated: number;
    rejected: number;
    /** Rows Arch sent that we hold no product for, or that this store does not stock. */
    unmatched: number;
    /** Products taken off sale / put back on sale at this store, from TradeOnline. */
    deactivated: number;
    reactivated: number;
    /** Products whose price changed: the open price row closed, a new one opened. */
    prices_updated: number;
    /** Rows carrying a price we would not write. The existing price was left alone. */
    prices_skipped: number;
    cursor_index: number;
    invocations: number;
    no_progress_invocations: number;
    started_at: string | null;
    finished_at: string | null;
    last_progress_at: string | null;
    last_error: string | null;
    triggered_by_user_id: string | null;
    created_at: string;
}

/** One product sync run. Global rather than per store, and it pages. */
export interface ProductSyncRun {
    id: string;
    kind: string;
    status: SyncStatus;
    trigger_type: SyncTrigger;
    phase: string | null;
    cursor_skip: number;
    take: number;
    pages_done: number;
    total_count: number | null;
    fetched: number;
    upserted: number;
    unchanged: number;
    rejected: number;
    /** Reconcile phase counters. */
    checked: number;
    deactivated: number;
    not_found: number;
    lookup_failed: number;
    /** Arch products on sale when the run finished. Null until SUCCEEDED. */
    live_products: number | null;
    invocations: number;
    no_progress_invocations: number;
    duration_ms: number | null;
    started_at: string | null;
    finished_at: string | null;
    last_heartbeat_at: string | null;
    last_error: string | null;
    triggered_by_user_id: string | null;
    created_at: string;
}

/**
 * A row a sync could not apply, kept with its raw payload so it can be chased down.
 *
 * Only malformed rows land here. Products we simply do not hold are counted on the
 * run instead — one row each would be tens of thousands every fifteen minutes.
 */
export interface RejectedProduct {
    id: string;
    run_id: string | null;
    external_product_id: number | null;
    sku: string | null;
    reason: string;
    details: string | null;
    payload: unknown;
    created_at: string;
    deleted_at: string | null;
}

export interface SyncStore {
    id: string;
    name: string;
    /** Arch's integer id. Null means nobody has mapped this store yet, so it is skipped. */
    external_store_id: number | null;
}

export async function fetchSyncStores(): Promise<SyncStore[]> {
    const { data, error } = await supabase
        .from('stores')
        .select('id, name, external_store_id')
        .order('name');

    if (error) throw error;
    return (data ?? []) as SyncStore[];
}

export async function fetchStockSyncRuns(limit = 50): Promise<StockSyncRun[]> {
    const { data, error } = await supabase
        .from('external_stock_sync_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

    if (error) throw error;
    return (data ?? []) as StockSyncRun[];
}

export async function fetchProductSyncRuns(limit = 50): Promise<ProductSyncRun[]> {
    const { data, error } = await supabase
        .from('external_product_sync_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

    if (error) throw error;
    return (data ?? []) as ProductSyncRun[];
}

export async function fetchRejectedProducts(limit = 200): Promise<RejectedProduct[]> {
    const { data, error } = await supabase
        .from('rejected_synced_products')
        .select('*')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);

    if (error) throw error;
    return (data ?? []) as RejectedProduct[];
}

/** The newest run per store, which is what "how fresh is this store" means. */
export function latestRunByStore(runs: StockSyncRun[]): Map<string, StockSyncRun> {
    const latest = new Map<string, StockSyncRun>();
    // Runs arrive newest first, so the first one seen for a store is its latest.
    for (const run of runs) {
        if (!latest.has(run.store_id)) latest.set(run.store_id, run);
    }
    return latest;
}

// ── Triggers ──────────────────────────────────────────────────────────────────
//
// All three functions accept an admin JWT and check the caller is admin/super
// themselves — the same predicate the RLS policies use. supabase-js attaches the
// session token, so there is nothing to pass.
//
// A run can take minutes. Callers should not block the UI on these promises: kick
// one off, then poll the run table, which is what it exists for.

/** What a sync reports back. Three of these are ordinary outcomes, not failures. */
export interface TriggerResult {
    status: 'SUCCEEDED' | 'FAILED' | 'IN_PROGRESS' | 'BUSY' | 'SKIPPED' | string;
    run_id: string | null;
    stopped_reason?: string | null;
    last_error?: string | null;
}

/**
 * Human wording for the outcomes that are not failures but are not "done" either.
 *
 * The product functions report lowercase statuses of their own — `busy` when a sweep
 * is already live, `no_run` when reconcile has nothing to work on — so both
 * vocabularies are handled here rather than assuming one shape.
 */
export function describeTriggerResult(result: TriggerResult): string {
    switch (result.status) {
        case 'busy':
            return 'A run is already in progress. Nothing was started.';
        case 'no_run':
            return 'There is no live run to reconcile. Run the product sync first.';
        case 'SUCCEEDED':
            return 'Sync finished.';
        case 'IN_PROGRESS':
            return 'Still running — it will carry on where it left off on the next scheduled run.';
        case 'BUSY':
            return 'A sync is already running for this store. Nothing was started.';
        case 'SKIPPED':
            return 'This store has no Arch store id, so there is nothing to sync yet.';
        case 'FAILED':
            return result.last_error ?? 'The sync failed. No stock was changed.';
        default:
            return `Finished with status ${result.status}.`;
    }
}

/**
 * A 401 here almost always means the session is stale rather than that anything is
 * broken — a local `supabase db reset` wipes auth.sessions while the browser keeps
 * holding its token. Worth saying so plainly: the raw error reads like a fault.
 */
function asFriendlyError(error: unknown): Error {
    const message = error instanceof Error ? error.message : String(error);
    if (/401|unauthor|invalid session|jwt/i.test(message)) {
        return new Error('Your session is no longer valid. Sign out and back in, then try again.');
    }
    if (/403|admin access/i.test(message)) {
        return new Error('Only an admin or super user can trigger a sync.');
    }
    if (/failed to fetch|networkerror|load failed/i.test(message)) {
        return new Error(
            'Could not reach the sync function. If you are running locally, check the Edge Functions are being served.',
        );
    }
    return error instanceof Error ? error : new Error(message);
}

export async function triggerStockSync(storeId: string): Promise<TriggerResult> {
    const { data, error } = await supabase.functions.invoke('sync-external-stock-data', {
        body: { store_id: storeId },
    });
    if (error) throw asFriendlyError(error);
    if (data?.error) throw asFriendlyError(new Error(data.error));
    return data as TriggerResult;
}

export async function triggerProductSync(): Promise<TriggerResult> {
    // Bodyless: the function's own defaults are the production values.
    const { data, error } = await supabase.functions.invoke('sync-external-product-data', {
        body: {},
    });
    if (error) throw asFriendlyError(error);
    if (data?.error) throw asFriendlyError(new Error(data.error));
    return data as TriggerResult;
}

export async function triggerProductReconcile(): Promise<TriggerResult> {
    const { data, error } = await supabase.functions.invoke('get-missing-product-data', {
        body: {},
    });
    if (error) throw asFriendlyError(error);
    if (data?.error) throw asFriendlyError(new Error(data.error));
    return data as TriggerResult;
}
